// MCP Security — Design tokens.
//
// Aligned 2026-10-12 with the WAF / Bot Management visual system
// (src/utils/colors.js). Border grays, radii, and the indigo primary
// now resolve to the exact same values the WAF Events table + the BM
// Dashboard use, so an operator switching modules sees one product.
//
// Palette source of truth: src/utils/colors.js. We do not import it
// to keep this file dependency-free, but every value below mirrors
// the WAF/BM token with the same name.
//
// Fed into components as `tokens.primaryIndigo`, `tokens.neutral[200]`
// etc. — pure data, no React.

const tokens = {
  // ── Primary (indigo) — mirrors colors.primary from utils/colors ──
  primary: {
    100: "#EFF1FE",   // colors.primary[100]
    200: "#828DF9",   // colors.primary[200]
    300: "#6371F7",   // colors.primary[300] — the WAF accent (ACCENT)
    400: "#4F5AC6",   // colors.primary[400]
    500: "#454FAD",   // colors.primary[500]
    alpha: "#EBEDF5", // colors.primary.alpha — hover tint
  },
  primaryIndigo: "#6371F7",     // == colors.primary[300]
  primaryIndigoInk: "#454FAD",  // == colors.primary[500]

  // ── Secondary (amber / yellow) ─────────────────────────────────
  secondary: {
    100: "#FFF6D0",
    200: "#F5C354",
    300: "#F2B429",
    400: "#C29021",
    500: "#916C19",
  },
  secondaryYellow: "#FFDB43",     // == colors.yellow[100]
  secondaryYellowDeep: "#DFB400", // == colors.yellow[200]

  // ── Semantic (status) ──────────────────────────────────────────
  success: {
    100: "#E8FBEF",
    500: "#1FC16B",  // == colors.green[200]
    600: "#1DD75B",
    bg: "#F6FCED",   // == colors.secondary.alpha
    border: "#DBF3B5",
  },
  warning: {
    500: "#F2B429",
    bg: "#FFF6D0",
  },
  danger: {
    500: "#FB3748",  // == colors.red[100]
    600: "#D00416",  // == colors.red[200]
    bg: "rgba(227,6,6,0.06)",
  },
  info: {
    500: "#0255ED",
    bg: "rgba(2,85,237,0.07)",
  },

  // ── Neutral greys — mirror colors.neutral from utils/colors ────
  // WAF uses colors.neutral[200] (#E5E5E5) as the canonical border
  // and divider gray; we align tokens.neutral[200..300] with that
  // single visual value so MCP cards & tables look identical.
  neutral: {
    0: "#FFFFFF",
    50: "#F8F9FA",   // == colors.neutral[50]
    100: "#FFFFFF",  // == colors.neutral[100] (WAF uses #FFFFFF here)
    150: "#F3F4F6",  // extra tint for inner row dividers
    200: "#E5E5E5",  // == colors.neutral[200] — border canonical
    300: "#E5E5E5",  // folded to [200] so inline borders match WAF
    400: "#B0B0B0",  // == colors.neutral[400]
    500: "#959595",  // == colors.neutral[500]
    600: "#7B7B7B",  // == colors.neutral[600]
    700: "#606060",  // == colors.neutral[700]
    800: "#000000",  // == colors.neutral[800]
    900: "#2B2B2B",  // == colors.neutral[900]
    1000: "#111111", // == colors.neutral[1000]
  },

  // ── Typefaces ──────────────────────────────────────────────────
  // WAF/BM use the app's root font (Rubik). Keep the same family
  // stack so a page using tokens.fontDisplay reads as the same text
  // as a page relying on the theme's default.
  fontSans:
    "'Rubik', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontDisplay: "'Rubik', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontMono:
    "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",

  // ── Shadow / focus ring ────────────────────────────────────────
  shadowFocus: "0 0 0 3px rgba(99,113,247,0.22)",
  shadowCard: "0 3px 50px rgba(0,0,0,0.07)",

  // ── Spacing / radius — align with WAF 8px card radius ──────────
  radius: 8,
  cardRadius: "8px",
  pillRadius: "999px",
};

export default tokens;

// Convenience aliases used all over the pages.
export const T = tokens;
